import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { RegistrationData } from '../../pages/RegisterPage';

interface ContactDetailsStepProps {
  data: RegistrationData;
  onDataChange: (data: Partial<RegistrationData>) => void;
  onNext: () => void;
  onBack: () => void;
}

const ContactDetailsStep: React.FC<ContactDetailsStepProps> = ({
  data,
  onDataChange,
  onNext,
  onBack,
}) => {
  const { t } = useTranslation();
  const [passwordError, setPasswordError] = useState('');

  const handleChange = (field: keyof RegistrationData) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const value = event.target.value;
    onDataChange({ [field]: value });

    // Validate password match
    if (field === 'confirmPassword' || field === 'password') {
      const password = field === 'password' ? value : data.password;
      const confirmPassword = field === 'confirmPassword' ? value : data.confirmPassword;
      
      if (confirmPassword && password !== confirmPassword) {
        setPasswordError('Паролите не съвпадат');
      } else {
        setPasswordError('');
      }
    }
  };

  const handleTermsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onDataChange({ termsAccepted: event.target.checked });
  };

  const canProceed = 
    data.firstName && 
    data.lastName && 
    data.email && 
    data.phone && 
    data.password && 
    data.confirmPassword && 
    data.password === data.confirmPassword &&
    data.termsAccepted &&
    !passwordError;

  return (
    <Box>
      <Typography
        variant="h5"
        sx={{
          textAlign: 'center',
          mb: 4,
          fontWeight: 600,
          color: '#ffffff',
        }}
      >
        {t('auth.register.contactDetails.title')}
      </Typography>

      <Typography
        variant="body1"
        sx={{
          textAlign: 'center',
          mb: 4,
          color: '#a0a0a0',
          maxWidth: 600,
          mx: 'auto',
        }}
      >
        {t('auth.register.contactDetails.subtitle')}
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label={t('auth.register.contactDetails.firstName')}
            value={data.firstName}
            onChange={handleChange('firstName')}
            required
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                '& fieldset': {
                  borderColor: '#2d2d44',
                },
                '&:hover fieldset': {
                  borderColor: '#28A0F0',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#28A0F0',
                },
              },
              '& .MuiInputLabel-root': {
                color: '#a0a0a0',
              },
              '& .MuiInputBase-input': {
                color: '#ffffff',
              },
            }}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label={t('auth.register.contactDetails.lastName')}
            value={data.lastName}
            onChange={handleChange('lastName')}
            required
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                '& fieldset': {
                  borderColor: '#2d2d44',
                },
                '&:hover fieldset': {
                  borderColor: '#28A0F0',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#28A0F0',
                },
              },
              '& .MuiInputLabel-root': {
                color: '#a0a0a0',
              },
              '& .MuiInputBase-input': {
                color: '#ffffff',
              },
            }}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label={t('auth.register.contactDetails.email')}
            type="email"
            value={data.email}
            onChange={handleChange('email')}
            required
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                '& fieldset': {
                  borderColor: '#2d2d44',
                },
                '&:hover fieldset': {
                  borderColor: '#28A0F0',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#28A0F0',
                },
              },
              '& .MuiInputLabel-root': {
                color: '#a0a0a0',
              },
              '& .MuiInputBase-input': {
                color: '#ffffff',
              },
            }}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label={t('auth.register.contactDetails.phone')}
            value={data.phone}
            onChange={handleChange('phone')}
            required
            placeholder={t('auth.register.contactDetails.phonePlaceholder')}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                '& fieldset': {
                  borderColor: '#2d2d44',
                },
                '&:hover fieldset': {
                  borderColor: '#28A0F0',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#28A0F0',
                },
              },
              '& .MuiInputLabel-root': {
                color: '#a0a0a0',
              },
              '& .MuiInputBase-input': {
                color: '#ffffff',
              },
            }}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label={t('auth.register.contactDetails.password')}
            type="password"
            value={data.password}
            onChange={handleChange('password')}
            required
            helperText={t('auth.register.contactDetails.passwordHelper')}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                '& fieldset': {
                  borderColor: '#2d2d44',
                },
                '&:hover fieldset': {
                  borderColor: '#28A0F0',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#28A0F0',
                },
              },
              '& .MuiInputLabel-root': {
                color: '#a0a0a0',
              },
              '& .MuiInputBase-input': {
                color: '#ffffff',
              },
              '& .MuiFormHelperText-root': {
                color: '#a0a0a0',
              },
            }}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label={t('auth.register.contactDetails.confirmPassword')}
            type="password"
            value={data.confirmPassword}
            onChange={handleChange('confirmPassword')}
            required
            error={!!passwordError}
            helperText={passwordError}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                '& fieldset': {
                  borderColor: passwordError ? '#ef4444' : '#2d2d44',
                },
                '&:hover fieldset': {
                  borderColor: passwordError ? '#ef4444' : '#28A0F0',
                },
                '&.Mui-focused fieldset': {
                  borderColor: passwordError ? '#ef4444' : '#28A0F0',
                },
              },
              '& .MuiInputLabel-root': {
                color: '#a0a0a0',
              },
              '& .MuiInputBase-input': {
                color: '#ffffff',
              },
              '& .MuiFormHelperText-root': {
                color: passwordError ? '#ef4444' : '#a0a0a0',
              },
            }}
          />
        </Grid>

        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Checkbox
                checked={data.termsAccepted}
                onChange={handleTermsChange}
                sx={{
                  color: '#28A0F0',
                  '&.Mui-checked': {
                    color: '#28A0F0',
                  },
                }}
              />
            }
            label={
              <Typography variant="body2" sx={{ color: '#a0a0a0' }}>
                {t('auth.register.contactDetails.termsAccepted')}
              </Typography>
            }
            sx={{ alignItems: 'flex-start' }}
          />
        </Grid>
      </Grid>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
        <Button
          variant="outlined"
          onClick={onBack}
          sx={{
            borderColor: '#2d2d44',
            color: '#ffffff',
            px: 4,
            py: 1.5,
            '&:hover': {
              borderColor: '#28A0F0',
              backgroundColor: 'rgba(40, 160, 240, 0.1)',
            },
          }}
        >
          {t('auth.register.contactDetails.back')}
        </Button>

        <Button
          variant="contained"
          onClick={onNext}
          disabled={!canProceed}
          sx={{
            background: 'linear-gradient(135deg, #28A0F0 0%, #ec4899 100%)',
            px: 6,
            py: 1.5,
            '&:hover': {
              background: 'linear-gradient(135deg, #1d4ed8 0%, #be185d 100%)',
            },
            '&:disabled': {
              background: '#2d2d44',
              color: '#6b7280',
            },
          }}
        >
          {t('auth.register.contactDetails.next')}
        </Button>
      </Box>
    </Box>
  );
};

export default ContactDetailsStep;
